import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { AtsChecker } from "@/components/AtsChecker";
import { ResumeExamples } from "@/components/ResumeExamples";
import { Benefits } from "@/components/Benefits";
import { HowItWorks } from "@/components/HowItWorks";
import { WhyDoneForYou } from "@/components/WhyDoneForYou";
import { Testimonials } from "@/components/Testimonials";
import { Pricing } from "@/components/Pricing";
import { Faq } from "@/components/Faq";
import { Contact } from "@/components/Contact";
import { FinalCta } from "@/components/FinalCta";
import { Footer } from "@/components/Footer";
import UploadResume from "@/pages/UploadResume";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import RefundPolicy from "@/pages/RefundPolicy";
import ResumeWritingService from "@/pages/ResumeWritingService";
import AiResumeBuilder from "@/pages/AiResumeBuilder";
import LoginPage from "@/pages/Login";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import VerifyEmail from "@/pages/VerifyEmail";
import FreeAtsChecker from "@/pages/FreeAtsChecker";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { Assistant } from "@/components/Assistant";
import { PENDING_PLAN_KEY } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// After a guest clicks a plan, signs in, and lands back here — resume checkout.
function useResumePendingCheckout() {
  const { user } = useAuth();
  React.useEffect(() => {
    if (!user || user === false) return;
    const pending = sessionStorage.getItem(PENDING_PLAN_KEY);
    if (!pending) return;
    sessionStorage.removeItem(PENDING_PLAN_KEY);
    axios
      .post(
        `${API}/payments/checkout`,
        { lookup_key: pending, origin_url: window.location.origin },
        { withCredentials: true }
      )
      .then(({ data }) => {
        window.location.href = data.checkout_url;
      })
      .catch(() => {
        toast.error("Couldn't resume checkout", {
          description: "Pick your plan again from the pricing section.",
        });
      });
  }, [user]);
}

function useMailtoFallback() {
  React.useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="mailto:"]');
      if (!a) return;
      e.preventDefault();
      const href = a.getAttribute("href") || "";
      const rest = href.slice("mailto:".length);
      const [emailPart, queryPart = ""] = rest.split("?");
      const email = decodeURIComponent(emailPart);
      const params = new URLSearchParams(queryPart);
      const subject = params.get("subject") || "";
      const body = params.get("body") || "";
      try {
        if (navigator.clipboard && navigator.clipboard.writeText)
          navigator.clipboard.writeText(email);
      } catch (_) {}
      const gmailParams = new URLSearchParams({ view: "cm", fs: "1", to: email });
      if (subject) gmailParams.set("su", subject);
      if (body) gmailParams.set("body", body);
      const gmailUrl = `https://mail.google.com/mail/?${gmailParams.toString()}`;
      const opened = window.open(gmailUrl, "_blank", "noopener,noreferrer");
      toast.success("Email copied to clipboard", {
        description: opened
          ? `${email} · opening Gmail in a new tab…`
          : `${email} · paste it in your email app`,
        duration: 4500,
      });
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);
}

function Landing() {
  useResumePendingCheckout();
  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="landing-root">
      <Navbar />
      <main>
        <Hero />
        <AtsChecker />
        <ResumeExamples />
        <Benefits />
        <HowItWorks />
        <WhyDoneForYou />
        <Testimonials />
        <Pricing />
        <Faq />
        <Contact />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  useMailtoFallback();
  return (
    <div className="App" data-testid="app-root">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/upload" element={<UploadResume />} />
            <Route path="/resume-writing-service" element={<ResumeWritingService />} />
            <Route path="/ai-resume-builder" element={<AiResumeBuilder />} />
            <Route path="/free-ats-checker" element={<FreeAtsChecker />} />
            <Route path="/login" element={<LoginPage mode="login" />} />
            <Route path="/signup" element={<LoginPage mode="register" />} />
            <Route path="/account" element={<Account />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/refund" element={<RefundPolicy />} />
          </Routes>
          <Assistant />
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-left" />
      </AuthProvider>
    </div>
  );
}

export default App;
